import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Plus, Edit, Trash2, ShieldAlert, X, Check } from 'lucide-react';
import { useState } from 'react';

import { PERMISSIONS, type Permission } from '@terra/contracts';
import { Button, EmptyState, Input, Spinner } from '@terra/ui';

import { api } from '../lib/api';

interface RoleFormData {
  name: string;
  description: string;
  permissions: Permission[];
}

export function RbacPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  const [formData, setFormData] = useState<RoleFormData>({
    name: '',
    description: '',
    permissions: [],
  });

  const queryClient = useQueryClient();

  const { data: roles, isLoading } = useQuery({
    queryKey: ['admin-roles'],
    queryFn: () => api.roles.listAdminRoles(),
  });

  const createMutation = useMutation({
    mutationFn: (data: RoleFormData) => api.roles.createAdminRole(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-roles'] });
      setIsModalOpen(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: { id: string; payload: RoleFormData }) =>
      api.roles.updateAdminRole(data.id, data.payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-roles'] });
      setIsModalOpen(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.roles.deleteAdminRole(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-roles'] });
    },
  });

  const handleOpenModal = (role?: any) => {
    if (role) {
      setEditingRoleId(role.id);
      setFormData({
        name: role.name,
        description: role.description,
        permissions: role.permissions,
      });
    } else {
      setEditingRoleId(null);
      setFormData({ name: '', description: '', permissions: [] });
    }
    setIsModalOpen(true);
  };

  const handleSave = () => {
    if (editingRoleId) {
      updateMutation.mutate({ id: editingRoleId, payload: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this role?')) {
      deleteMutation.mutate(id);
    }
  };

  const togglePermission = (perm: Permission) => {
    setFormData((prev) => {
      const perms = prev.permissions.includes(perm)
        ? prev.permissions.filter((p) => p !== perm)
        : [...prev.permissions, perm];
      return { ...prev, permissions: perms };
    });
  };

  return (
    <div className="space-y-6 pb-8">
      <div className="mb-2 flex justify-end">
        <button
          onClick={() => handleOpenModal()}
          className="bg-ink hover:bg-ink/90 flex h-10 items-center justify-center rounded-xl px-4 text-[13px] font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
        >
          <Plus className="mr-2 h-4 w-4" /> Create Role
        </button>
      </div>

      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="p-0">
          {isLoading ? (
            <div className="grid min-h-[300px] place-items-center">
              <Spinner size="lg" />
            </div>
          ) : !roles || roles.length === 0 ? (
            <div className="py-12">
              <EmptyState
                icon={<ShieldAlert className="text-ink-muted h-10 w-10" />}
                title="No custom roles found"
                description="Create a custom role to assign specific permissions to admin staff."
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-border/60 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                    <th className="py-3 pl-5 pr-4">Role Name</th>
                    <th className="py-3 pr-4">Description</th>
                    <th className="py-3 pr-4">Permissions</th>
                    <th className="py-3 pr-4">Created At</th>
                    <th className="py-3 pl-4 pr-5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-border/40 divide-y text-[13px]">
                  <AnimatePresence>
                    {roles.map((role, idx) => (
                      <motion.tr
                        key={role.id}
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.02 }}
                        className="hover:bg-surface-subtle/30 group transition-colors"
                      >
                        <td className="py-3 pl-5 pr-4">
                          <div className="flex items-center gap-3">
                            <div className="bg-surface-subtle text-ink-muted flex h-9 w-9 shrink-0 items-center justify-center rounded-xl font-bold">
                              <Shield className="h-4 w-4" strokeWidth={2} />
                            </div>
                            <div className="min-w-0">
                              <p className="text-ink truncate font-semibold">{role.name}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 pr-4">
                          <p className="text-ink-muted max-w-xs truncate">
                            {role.description || '—'}
                          </p>
                        </td>
                        <td className="py-3 pr-4">
                          <span className="bg-surface-subtle text-ink-muted inline-flex h-6 items-center justify-center rounded-md px-2 text-[11px] font-bold">
                            {role.permissions.length} permissions
                          </span>
                        </td>
                        <td className="text-ink-muted py-3 pr-4">
                          {format(new Date(role.createdAt), 'MMM dd, yyyy')}
                        </td>
                        <td className="py-3 pl-4 pr-5">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => handleOpenModal(role)}
                              className="text-ink-muted hover:text-ink hover:bg-surface-subtle flex h-8 w-8 items-center justify-center rounded-lg transition-colors"
                              title="Edit Role"
                            >
                              <Edit className="h-[18px] w-[18px]" strokeWidth={2} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(role.id)}
                              className="flex h-8 w-8 items-center justify-center rounded-lg text-red-400 transition-colors hover:bg-red-50 hover:text-red-600"
                              title="Delete Role"
                              disabled={deleteMutation.isPending}
                            >
                              <Trash2 className="h-[18px] w-[18px]" strokeWidth={2} />
                            </button>
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Role Slide-over drawer ── */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex justify-end">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsModalOpen(false)}
              className="bg-ink/40 absolute inset-0 backdrop-blur-sm"
            />
            <motion.div
              initial={{ x: '100%', opacity: 0.5 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: '100%', opacity: 0.5 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="border-border/60 bg-surface relative flex h-full w-full max-w-lg flex-col overflow-y-auto border-l shadow-2xl"
            >
              {/* Header */}
              <div className="border-border/60 bg-surface/80 sticky top-0 z-10 flex items-center justify-between border-b px-6 py-5 backdrop-blur-md">
                <div>
                  <h3 className="text-ink text-lg font-bold tracking-tight">
                    {editingRoleId ? 'Edit Role' : 'Create Role'}
                  </h3>
                  <p className="text-ink-subtle text-xs font-medium">
                    Define a custom role and assign permissions.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="bg-surface-subtle text-ink-muted hover:text-ink hover:bg-surface-subtle/80 flex h-8 w-8 items-center justify-center rounded-full transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 space-y-6 p-6">
                <div className="space-y-4">
                  <Input
                    label="Role Name"
                    placeholder="e.g., Support Manager"
                    value={formData.name}
                    onChange={(e: any) => setFormData({ ...formData, name: e.target.value })}
                  />
                  <div>
                    <label className="text-ink mb-1.5 block text-[13px] font-semibold">
                      Description
                    </label>
                    <textarea
                      className="border-border/60 bg-surface text-ink focus:border-gold-400 focus:ring-gold-400 min-h-[80px] w-full resize-none rounded-xl border p-3 text-[13px] font-medium transition-colors focus:outline-none focus:ring-1"
                      placeholder="Describe the purpose of this role"
                      value={formData.description}
                      onChange={(e: any) =>
                        setFormData({ ...formData, description: e.target.value })
                      }
                      rows={3}
                    />
                  </div>
                </div>

                <div>
                  <h4 className="text-ink mb-3 text-[13px] font-semibold">Permissions</h4>
                  <div className="border-border/60 bg-surface-subtle/30 flex max-h-[400px] flex-col gap-2 overflow-y-auto rounded-xl border p-2">
                    {PERMISSIONS.map((perm) => {
                      const isChecked = formData.permissions.includes(perm);
                      return (
                        <label
                          key={perm}
                          className={`border-border/60 group flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                            isChecked
                              ? 'bg-surface border-gold-500/50 shadow-sm'
                              : 'hover:bg-surface'
                          }`}
                        >
                          <div className="relative mt-0.5 flex h-4 w-4 items-center justify-center">
                            <input
                              type="checkbox"
                              className="peer absolute h-full w-full cursor-pointer opacity-0"
                              checked={isChecked}
                              onChange={() => togglePermission(perm)}
                            />
                            <div className="border-border/60 peer-checked:bg-gold-500 pointer-events-none flex h-4 w-4 rounded transition-colors peer-checked:border-none">
                              {isChecked && (
                                <Check className="m-auto h-3 w-3 text-white" strokeWidth={3} />
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col">
                            <span
                              className={`text-[13px] font-bold ${isChecked ? 'text-ink' : 'text-ink-muted group-hover:text-ink'}`}
                            >
                              {perm}
                            </span>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Sticky Footer */}
              <div className="border-border/60 bg-surface sticky bottom-0 flex justify-end gap-3 border-t p-6 shadow-[0_-4px_20px_-10px_rgba(0,0,0,0.1)]">
                <Button variant="outline" onClick={() => setIsModalOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={
                    !formData.name.trim() ||
                    formData.permissions.length === 0 ||
                    createMutation.isPending ||
                    updateMutation.isPending
                  }
                  className="bg-ink hover:bg-ink/90 text-white"
                >
                  {editingRoleId ? 'Save Changes' : 'Create Role'}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
